/**
 * OnlyOffice 文件预览工具
 * 移植自 Happy 项目，适配 IM Web 环境
 */

/**
 * OnlyOffice API 配置
 * 复用项目已有的 VNC API Base URL 配置（与 vncApi.js 保持一致）
 */
const ONLYOFFICE_CONFIG = {
    // 使用环境变量配置的 VNC API Base URL
    API_BASE: (process.env.VUE_APP_VNC_API_BASE_URL || 'https://api.vnc.service.thinkgs.cn') + '/api/v1',
    // OnlyOffice 服务公网地址将从 API 响应中获取
};

/**
 * 获取文件扩展名
 */
function getFileExtension(filename) {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/**
 * 判断是否是 Office 文件
 */
export function isOfficeFile(filename) {
    const ext = getFileExtension(filename);
    return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf'].includes(ext);
}

/**
 * HTML 转义辅助函数
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * 在 OnlyOffice 中打开文件
 *
 * @param {Object} options - 配置参数
 * @param {string} options.fileUrl - 文件 Minio URL (如: https://file.s3.service.thinkgs.cn/box-im/file/20251206/1765010426068.xlsx)
 * @param {string} options.fileName - 文件名 (如: document.docx)
 * @param {number} options.userId - 用户ID
 * @param {string} options.token - 认证 token
 * @param {boolean} [options.readonly=false] - 是否只读模式
 */
export async function openInOnlyOffice({ fileUrl, fileName, userId, token, readonly = false }) {
    // 验证必要参数
    if (!userId) {
        throw new Error('User ID not available. Please ensure you are logged in.');
    }

    if (!token) {
        throw new Error('Token not available. Please ensure you are logged in.');
    }

    if (!fileUrl) {
        throw new Error('File URL is required.');
    }

    if (!fileName) {
        throw new Error('File name is required.');
    }

    const podName = `vnc-${userId}`;

    // 调用后端 API 获取 OnlyOffice 配置
    const apiUrl = `${ONLYOFFICE_CONFIG.API_BASE}/office/preview-config?pod_name=${podName}&file_url=${encodeURIComponent(fileUrl)}&readonly=${readonly}`;

    const response = await fetch(apiUrl, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get OnlyOffice config: ${response.status} ${response.statusText}\n${errorText}`);
    }

    const data = await response.json();

    // 使用后端返回的 OnlyOffice URL（支持内外网自动适配）
    const onlyofficeUrl = data.onlyoffice_url;
    const apiJsUrl = `${onlyofficeUrl}/web-apps/apps/api/documents/api.js`;

    // 获取实际的 OnlyOffice 配置（处理嵌套的 config 结构）
    const editorConfig = data.config.config || data.config;

    // 打开新窗口并集成 OnlyOffice Editor
    const editorWindow = window.open('', '_blank');
    if (!editorWindow) {
        throw new Error('Failed to open new window. Please allow popups for this site.');
    }

    // 构建 HTML 页面
    // 使用原始文件名作为标题，而不是后端返回的 title
    const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>${escapeHtml(fileName)}</title>
                <style>
                    html, body {
                        margin: 0;
                        padding: 0;
                        width: 100%;
                        height: 100%;
                        overflow: hidden;
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
                    }
                    #placeholder {
                        width: 100%;
                        height: 100%;
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                    }
                    /* OnlyOffice iframe 样式 */
                    #placeholder iframe {
                        width: 100% !important;
                        height: 100% !important;
                        border: none !important;
                    }
                    #loading {
                        position: fixed;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        background: #f5f5f5;
                        z-index: 9999;
                    }
                    #loading .spinner {
                        width: 50px;
                        height: 50px;
                        border: 4px solid #e0e0e0;
                        border-top-color: #4A90E2;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                    }
                    #loading .text {
                        margin-top: 20px;
                        color: #666;
                        font-size: 14px;
                    }
                    #error {
                        display: none;
                        position: fixed;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        background: white;
                        padding: 30px;
                        border-radius: 8px;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                        max-width: 500px;
                        text-align: center;
                    }
                    #error h3 {
                        color: #E74C3C;
                        margin: 0 0 15px 0;
                    }
                    #error p {
                        color: #666;
                        margin: 0 0 20px 0;
                        line-height: 1.5;
                    }
                    #error button {
                        background: #4A90E2;
                        color: white;
                        border: none;
                        padding: 10px 20px;
                        border-radius: 4px;
                        cursor: pointer;
                        font-size: 14px;
                    }
                    #error button:hover {
                        background: #357ABD;
                    }
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                </style>
            </head>
            <body>
                <div id="loading">
                    <div class="spinner"></div>
                    <div class="text">正在加载编辑器...</div>
                </div>
                <div id="error">
                    <h3>加载失败</h3>
                    <p id="error-message"></p>
                    <button onclick="window.close()">关闭窗口</button>
                </div>
                <div id="placeholder"></div>
                <script>
                    (function() {
                        var config = ${JSON.stringify(editorConfig)};
                        var apiJsUrl = ${JSON.stringify(apiJsUrl)};

                        function showError(message) {
                            document.getElementById('loading').style.display = 'none';
                            document.getElementById('error').style.display = 'block';
                            document.getElementById('error-message').textContent = message;
                        }

                        function loadOnlyOffice() {
                            // 动态加载 OnlyOffice API 脚本
                            var script = document.createElement('script');
                            script.type = 'text/javascript';
                            script.src = apiJsUrl;

                            script.onload = function() {
                                try {
                                    document.getElementById('loading').style.display = 'none';

                                    // 初始化编辑器
                                    var editor = new DocsAPI.DocEditor('placeholder', config);
                                } catch (err) {
                                    showError('初始化编辑器失败: ' + err.message + '\\n\\nConfig: ' + JSON.stringify(config, null, 2));
                                }
                            };

                            script.onerror = function() {
                                showError('无法加载 OnlyOffice API。请检查网络连接或联系管理员。\\n\\n服务地址: ' + apiJsUrl);
                            };

                            document.head.appendChild(script);
                        }

                        // 页面加载完成后执行
                        if (document.readyState === 'loading') {
                            document.addEventListener('DOMContentLoaded', loadOnlyOffice);
                        } else {
                            loadOnlyOffice();
                        }
                    })();
                </script>
            </body>
            </html>
        `;

    editorWindow.document.open();
    editorWindow.document.write(htmlContent);
    editorWindow.document.close();

    return true;
}

/**
 * 从 IM 消息中打开 Office 文件
 * 自动从 Vue 环境获取用户信息和构造文件路径
 *
 * @param {Object} options - 配置参数
 * @param {Object} options.msgInfo - 消息对象
 * @param {Object} options.store - Vuex store 实例 (可选，如果不提供则使用 sessionStorage)
 */
export async function openOfficeFileFromMessage({ msgInfo, store }) {
    // 1. 获取用户认证信息
    let userId, token;

    if (store && store.state && store.state.userStore) {
        // 从 Vuex store 获取
        userId = store.state.userStore.userInfo.id;
    }

    // 从 sessionStorage 获取 tokenKey (与 vncApi.js 保持一致)
    token = sessionStorage.getItem('tokenKey');

    // 检查是否在 VSCode 环境中
    if (window.vscodeServices && window.vscodeServices.get) {
        try {
            const userStateService = window.vscodeServices.get('IUserStateService');
            const user = userStateService.currentUser;
            if (user) {
                userId = userId || user.id;
                // VSCode 环境中优先使用 tokenKey，如果没有则使用 accessToken
                if (!token && user.accessToken) {
                    token = user.accessToken;
                }
            }
        } catch (e) {
            // 忽略错误
        }
    }

    if (!userId) {
        throw new Error('无法获取用户ID，请确保已登录');
    }

    if (!token) {
        throw new Error('无法获取认证Token，请重新登录');
    }

    // 2. 解析文件信息
    let fileData;
    try {
        fileData = typeof msgInfo.content === 'string'
            ? JSON.parse(msgInfo.content)
            : msgInfo.content;
    } catch (e) {
        throw new Error('无法解析文件信息：' + e.message);
    }

    const fileName = fileData.name;
    const fileUrl = fileData.url;  // Minio 文件 URL

    if (!fileName) {
        throw new Error('文件名不存在');
    }

    if (!fileUrl) {
        throw new Error('文件 URL 不存在');
    }

    // 检查是否是 Office 文件
    if (!isOfficeFile(fileName)) {
        throw new Error('不是 Office 文件类型');
    }

    // 3. 调用 OnlyOffice 打开文件（直接使用 Minio URL）
    await openInOnlyOffice({
        fileUrl,           // 传递 Minio URL
        fileName,          // 显示原始文件名
        userId,
        token,
        readonly: false
    });

    return true;
}

/**
 * 生成文档 Key（使用文件URL的SHA-256哈希）
 * OnlyOffice 使用 key 来标识文档版本，相同的 key 表示相同的文档
 *
 * @param {string} fileUrl - 文件URL
 * @returns {Promise<string>} 文档key（64位十六进制字符串）
 */
async function generateFileKey(fileUrl) {
    // 使用 Web Crypto API 生成 SHA-256 哈希
    const encoder = new TextEncoder();
    const data = encoder.encode(fileUrl);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
}

/**
 * 根据文件扩展名获取正确的MIME类型
 *
 * @param {string} fileType - 文件扩展名
 * @returns {string} MIME类型
 */
function getMimeType(fileType) {
    const mimeTypes = {
        // Word
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'odt': 'application/vnd.oasis.opendocument.text',
        'rtf': 'application/rtf',
        'txt': 'text/plain',

        // Excel
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'ods': 'application/vnd.oasis.opendocument.spreadsheet',
        'csv': 'text/csv',

        // PowerPoint
        'ppt': 'application/vnd.ms-powerpoint',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'odp': 'application/vnd.oasis.opendocument.presentation',

        // PDF
        'pdf': 'application/pdf'
    };

    return mimeTypes[fileType.toLowerCase()] || 'application/octet-stream';
}

/**
 * 根据文件扩展名判断文档类型
 * OnlyOffice 支持三种文档类型: word, cell, slide
 *
 * @param {string} fileType - 文件扩展名（如 docx, xlsx, pptx）
 * @returns {string} 文档类型（word/cell/slide）
 */
function getDocumentType(fileType) {
    const wordTypes = ['doc', 'docx', 'txt', 'rtf', 'odt'];
    const cellTypes = ['xls', 'xlsx', 'csv', 'ods'];
    const slideTypes = ['ppt', 'pptx', 'odp'];

    const ext = fileType.toLowerCase();

    if (wordTypes.includes(ext)) return 'word';
    if (cellTypes.includes(ext)) return 'cell';
    if (slideTypes.includes(ext)) return 'slide';

    // 默认返回 word
    return 'word';
}

/**
 * 直接使用Minio URL在OnlyOffice中预览文件（不经过后端API）
 * 适用场景：文件已存储在Minio，只需要预览（只读模式），不需要编辑保存
 *
 * 优势：
 * - 不需要后端API支持
 * - 不需要后端中转文件
 * - 减少服务器负载
 * - 预览速度更快
 *
 * @param {Object} options - 配置参数
 * @param {string} options.fileUrl - 文件Minio URL (必须能被OnlyOffice服务器访问)
 * @param {string} options.fileName - 文件名
 * @param {number} [options.userId] - 用户ID（可选，用于标识用户）
 * @param {string} [options.userName] - 用户名（可选，显示在编辑器中）
 * @returns {Promise<boolean>}
 */
export async function openInOnlyOfficeDirectly({ fileUrl, fileName, userId, userName }) {
    // 验证必要参数
    if (!fileUrl) {
        throw new Error('文件URL不能为空');
    }

    if (!fileName) {
        throw new Error('文件名不能为空');
    }

    // 检查是否是 Office 文件
    if (!isOfficeFile(fileName)) {
        throw new Error('不是 Office 文件类型');
    }

    // 1. 生成文档key（使用文件URL+时间戳的哈希值，避免缓存问题）
    // 注意：加入时间戳后每次打开都会是新文档，不会使用OnlyOffice的缓存
    const uniqueUrl = fileUrl + '?' + Date.now();
    const documentKey = await generateFileKey(uniqueUrl);

    // 2. 获取文件类型和文档类型
    const fileType = getFileExtension(fileName);
    const documentType = getDocumentType(fileType);
    const mimeType = getMimeType(fileType);

    // 3. OnlyOffice服务器地址（使用环境变量配置）
    const onlyofficeUrl = process.env.VUE_APP_ONLYOFFICE_URL || 'https://onlyoffice.vnc.service.thinkgs.cn';
    const apiJsUrl = `${onlyofficeUrl}/web-apps/apps/api/documents/api.js`;

    // 3.5 将Minio的公网URL转换为内网URL（用于OnlyOffice服务器下载）
    // 公网地址：https://file.s3.service.thinkgs.cn/box-im/file/...
    // 内网地址：http://192.168.10.254:9538/box-im/file/...
    // 并添加 response-content-type 参数，让Minio返回正确的Content-Type
    const internalFileUrl = fileUrl.replace(
        'https://file.s3.service.thinkgs.cn',
        'http://192.168.10.254:9538'
    ) + `?response-content-type=${encodeURIComponent(mimeType)}`;


    // 4. 构造OnlyOffice配置（只读预览模式）
    const editorConfig = {
        documentType: documentType,
        document: {
            title: fileName,
            url: internalFileUrl,  // 使用内网Minio URL，加速下载！
            fileType: fileType,
            key: documentKey,
            permissions: {
                edit: false,      // 只读模式
                download: true,
                print: true,
                review: false,
                comment: false
            }
        },
        editorConfig: {
            mode: 'view',        // 只读模式
            lang: 'zh-CN',
            user: userId && userName ? {
                id: String(userId),
                name: userName
            } : undefined,
            customization: {
                autosave: false,
                forcesave: false,
                comments: false,
                zoom: 100
            }
        }
    };

    // 5. 打开新窗口
    const editorWindow = window.open('', '_blank');
    if (!editorWindow) {
        throw new Error('无法打开新窗口，请允许弹出窗口');
    }

    // 6. 构建HTML页面
    const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>${escapeHtml(fileName)} - 预览</title>
            <style>
                html, body {
                    margin: 0;
                    padding: 0;
                    width: 100%;
                    height: 100%;
                    overflow: hidden;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
                }
                #placeholder {
                    width: 100%;
                    height: 100%;
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                }
                #placeholder iframe {
                    width: 100% !important;
                    height: 100% !important;
                    border: none !important;
                }
                #loading {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background: #f5f5f5;
                    z-index: 9999;
                }
                #loading .spinner {
                    width: 50px;
                    height: 50px;
                    border: 4px solid #e0e0e0;
                    border-top-color: #4A90E2;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                #loading .text {
                    margin-top: 20px;
                    color: #666;
                    font-size: 14px;
                }
                #error {
                    display: none;
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    background: white;
                    padding: 30px;
                    border-radius: 8px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    max-width: 500px;
                    text-align: center;
                }
                #error h3 {
                    color: #E74C3C;
                    margin: 0 0 15px 0;
                }
                #error p {
                    color: #666;
                    margin: 0 0 20px 0;
                    line-height: 1.5;
                    white-space: pre-wrap;
                    text-align: left;
                }
                #error button {
                    background: #4A90E2;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 14px;
                }
                #error button:hover {
                    background: #357ABD;
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            </style>
        </head>
        <body>
            <div id="loading">
                <div class="spinner"></div>
                <div class="text">正在加载文档预览...</div>
            </div>
            <div id="error">
                <h3>加载失败</h3>
                <p id="error-message"></p>
                <button onclick="window.close()">关闭窗口</button>
            </div>
            <div id="placeholder"></div>
            <script>
                (function() {
                    var config = ${JSON.stringify(editorConfig)};
                    var apiJsUrl = ${JSON.stringify(apiJsUrl)};

                    function showError(message) {
                        document.getElementById('loading').style.display = 'none';
                        document.getElementById('error').style.display = 'block';
                        document.getElementById('error-message').textContent = message;
                    }

                    function loadOnlyOffice() {
                        // 动态加载 OnlyOffice API 脚本
                        var script = document.createElement('script');
                        script.type = 'text/javascript';
                        script.src = apiJsUrl;

                        script.onload = function() {
                            try {
                                document.getElementById('loading').style.display = 'none';

                                // 初始化编辑器（预览模式）
                                var editor = new DocsAPI.DocEditor('placeholder', config);

                                console.log('OnlyOffice Editor initialized successfully');
                                console.log('Document URL:', config.document.url);
                                console.log('Document Key:', config.document.key);
                            } catch (err) {
                                console.error('Failed to initialize OnlyOffice:', err);
                                showError('初始化编辑器失败: ' + err.message);
                            }
                        };

                        script.onerror = function() {
                            showError('无法加载 OnlyOffice API\\n\\n服务地址: ' + apiJsUrl + '\\n\\n请检查网络连接或联系管理员。');
                        };

                        document.head.appendChild(script);
                    }

                    // 页面加载完成后执行
                    if (document.readyState === 'loading') {
                        document.addEventListener('DOMContentLoaded', loadOnlyOffice);
                    } else {
                        loadOnlyOffice();
                    }
                })();
            </script>
        </body>
        </html>
    `;

    editorWindow.document.open();
    editorWindow.document.write(htmlContent);
    editorWindow.document.close();

    return true;
}

/**
 * 从IM消息中直接打开Office文件预览（不经过后端API）
 * 适用于Minio存储的文件，直接使用文件URL预览
 *
 * @param {Object} options - 配置参数
 * @param {Object} options.msgInfo - 消息对象
 * @param {Object} [options.store] - Vuex store实例（可选，用于获取用户信息）
 * @returns {Promise<boolean>}
 */
export async function openOfficeFileFromMessageDirectly({ msgInfo, store }) {
    // 1. 获取用户信息（可选，用于避免匿名弹窗）
    let userId, userName;

    if (store && store.state && store.state.userStore) {
        // 从 Vuex store 获取用户信息
        const userInfo = store.state.userStore.userInfo;
        userId = userInfo.id;
        userName = userInfo.nickName || userInfo.userName;
    }

    // 检查是否在 VSCode 环境中
    if (window.vscodeServices && window.vscodeServices.get) {
        try {
            const userStateService = window.vscodeServices.get('IUserStateService');
            const user = userStateService.currentUser;
            if (user) {
                userId = userId || user.id;
                userName = userName || user.nickName || user.userName;
            }
        } catch (e) {
            // 忽略错误
        }
    }

    // 2. 解析文件信息
    let fileData;
    try {
        fileData = typeof msgInfo.content === 'string'
            ? JSON.parse(msgInfo.content)
            : msgInfo.content;
    } catch (e) {
        throw new Error('无法解析文件信息：' + e.message);
    }

    const fileName = fileData.name;
    const fileUrl = fileData.url;  // Minio 文件 URL

    if (!fileName) {
        throw new Error('文件名不存在');
    }

    if (!fileUrl) {
        throw new Error('文件 URL 不存在');
    }

    // 3. 调用直接预览函数
    await openInOnlyOfficeDirectly({
        fileUrl,
        fileName,
        userId,
        userName
    });

    return true;
}
